/**
 * The scope sentence's two pickers, as pure axis math.
 *
 * Adapted from upstream's `settingsScopeAxis.ts` (pingdotgg/t3code, MIT, pin
 * e3e7cc3f), minus the checkout axis forge's shell does not carry. Choosing
 * one axis keeps the other: the two pickers narrow one target from two
 * directions, so neither may silently clear the other's choice.
 *
 * @module
 */

import type { SettingsScopeEnvironment, SettingsScopeSearch } from "./settingsScope";

export const ALL_ENVIRONMENTS_VALUE = "all";
export const ALL_PROJECTS_VALUE = "all";

/**
 * Two environments may share a label (two machines both named "Development"),
 * so a duplicate label is disambiguated by id in the menu. Forge's
 * environment rows carry no display URL; the id is the address the runtime
 * already knows.
 */
export function settingsScopeEnvironmentLabel(
  environment: SettingsScopeEnvironment,
  environments: readonly SettingsScopeEnvironment[],
): string {
  const duplicate = environments.some(
    (other) =>
      other.environmentId !== environment.environmentId && other.label === environment.label,
  );
  return duplicate ? `${environment.label} · ${environment.environmentId}` : environment.label;
}

/** The environment axis: `all` or an environment id. */
export function environmentAxisValue(search: SettingsScopeSearch): string {
  return search.machine ?? ALL_ENVIRONMENTS_VALUE;
}

/** The project axis: `all` or a project key. */
export function projectAxisValue(search: SettingsScopeSearch): string {
  return search.project ?? ALL_PROJECTS_VALUE;
}

/** Choosing an environment keeps the project. */
export function selectEnvironmentAxis(
  search: SettingsScopeSearch,
  value: string,
): SettingsScopeSearch {
  return {
    ...(search.project === undefined ? {} : { project: search.project }),
    ...(value === ALL_ENVIRONMENTS_VALUE ? {} : { machine: value }),
  };
}

/** Choosing a project keeps the environment axis. */
export function selectProjectAxis(search: SettingsScopeSearch, value: string): SettingsScopeSearch {
  return {
    ...(value === ALL_PROJECTS_VALUE ? {} : { project: value }),
    ...(search.machine === undefined ? {} : { machine: search.machine }),
  };
}
